import React, { useState } from 'react';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack';
import Checkbox from '@grnhse/seedling/lib/birch/components/checkbox';
import Collapsible from '@grnhse/seedling/lib/birch/components/collapsible';
import DropdownArrowIcon from '@grnhse/seedling/lib/birch/icons/dropdown_arrow';
import IconButton from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import {
  SectionTitle,
  SecondaryBody,
} from '@grnhse/seedling/lib/birch/typography';
import FamilyTierPills from './family_tier_pills';
import ScopeResourceRow from './scope_resource_row';
import { familyIcon } from './family_icons';
import { allScopeNames, selectionState } from './selection';
import {
  ArrowIconWrapper,
  FamilyHeader,
  FamilyTitleBlock,
  VisuallyHidden,
} from './styled_components';
import { ScopeFamily } from './types';

const HiddenLabel = ({ label }: { label: string }) => (
  <VisuallyHidden>{label}</VisuallyHidden>
);

type ReadOnlyProps = {
  family: ScopeFamily;
  readOnly: true;
};

type EditableProps = {
  family: ScopeFamily;
  readOnly?: false;
  selected: Set<string>;
  onToggleScope: (name: string) => void;
  onSetScopes: (names: string[], checked: boolean) => void;
};

type Props = ReadOnlyProps | EditableProps;

export default function ScopeFamilySection(props: Props) {
  const { family } = props;
  const [isOpen, setIsOpen] = useState(false);
  const names = allScopeNames([family]);
  const state = props.readOnly ? null : selectionState(names, props.selected);
  const Icon = familyIcon(family.key);

  return (
    <div>
      <FamilyHeader onClick={() => setIsOpen((open) => !open)}>
        {!props.readOnly && (
          // Not interactive itself — only keeps checkbox clicks from toggling the collapse.
          // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
          <div onClick={(e) => e.stopPropagation()}>
            <Checkbox
              checked={state === 'all'}
              indeterminate={state === 'some'}
              onChange={(checked: boolean) => props.onSetScopes(names, checked)}
              label={family.label}
              CustomLabel={HiddenLabel}
              value={family.key}
            />
          </div>
        )}
        <Icon ariaHidden />
        <FamilyTitleBlock>
          <SectionTitle>{family.label}</SectionTitle>
          <SecondaryBody>{family.description}</SecondaryBody>
        </FamilyTitleBlock>
        {props.readOnly ? (
          <FamilyTierPills family={family} readOnly />
        ) : (
          <FamilyTierPills family={family} selected={props.selected} />
        )}
        <ArrowIconWrapper $isOpen={isOpen}>
          <IconButton
            icon={DropdownArrowIcon}
            label={`Toggle ${family.label}`}
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              setIsOpen((open) => !open);
            }}
          />
        </ArrowIconWrapper>
      </FamilyHeader>
      <Collapsible id={`scope-family-${family.key}`} isOpen={isOpen}>
        <Stack spacing={2} sx={{ marginTop: 1, paddingLeft: '24px' }}>
          {family.resources.map((resource) =>
            props.readOnly ? (
              <ScopeResourceRow
                key={resource.key}
                resource={resource}
                readOnly
              />
            ) : (
              <ScopeResourceRow
                key={resource.key}
                resource={resource}
                selected={props.selected}
                onToggleScope={props.onToggleScope}
                onSetScopes={props.onSetScopes}
              />
            )
          )}
        </Stack>
      </Collapsible>
    </div>
  );
}
